import { MessageBar, ProgressIndicator, Stack } from "@fluentui/react";
import { minutesToMilliseconds, secondsToMilliseconds } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { ContractStatus } from "../../enums";

import { ActionConfirmDialog } from "./components/ActionConfirmDialog";
import { ClientDrawer } from "./components/ClientDrawer";
import { OccurrencesPanel } from "./components/OccurrencesPanel";
import { OwnershipTransferTermDraftDrawer } from "./components/OwnershipTransferTermDraftDrawer";
import { ACTIONS_REQUIRING_REASON } from "./constants";

import { PermissionsAction } from "@/common/types/permissions.types";
import { IInvoiceInfos } from "@/components/Contracts/components/Invoice/interfaces";
import type {
  Contract,
  ContractApprovalFlowResponse
} from "@/components/Contracts/contract.interface";
import ContractProductsRenewalModal from "@/components/Contracts/ContractProductsRenewal/ContractProductsRenewalModal";
import { DisplayContractInfo } from "@/components/Contracts/DisplayContractInfo";
import { UploadDocumentModal } from "@/components/Contracts/UploadDocumentModal";
import { ViewMenu } from "@/components/Contracts/ViewMenu";
import { ViewProgress } from "@/components/Contracts/ViewProgress";
import { ApprovalFlowShimmer } from "@/components/Contracts/ViewProgress/ApprovalFlowShimmer";
import { DrawerActions } from "@/components/Shared/CustomDrawer";
import { Loader } from "@/components/Shared/Loader";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import {
  IMessageBar,
  MessageBar as MessageBarCustom,
  messageBarTypes
} from "@/core/libs/message-bar";
import { useConfirmModal } from "@/hooks/useConfirmModal";
import useQueryParams from "@/hooks/useQueryParams";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { changeContractStatus, getContract } from "@/modules/Contracts/api";
import type { IStep } from "@/modules/Terms/interfaces/ITerm.interface";
import { sleep } from "@/utils/Sleep";

interface DrawerState {
  action: DrawerActions;
  isOpen: boolean;
}

export function ContractView(): JSX.Element {
  const isReloading = useRef(false);
  const { openModalWithSettings } = useConfirmModal();

  const drawerContainerRef = useRef<HTMLDivElement>(null);

  const [statusChangeReason, setStatusChangeReason] = useState<string>("");
  const [activeStatusChangeRequest, setActiveStatusChangeReason] =
    useState<boolean>(false);
  const [action, setAction] = useState<PermissionsAction>(null);
  const [actionMessage, setActionMessage] = useState<string>(null);
  const [message, setMessage] = useState<IMessageBar>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [nextStep, setNextStep] = useState<IStep>();
  const [contract, setContract] = useState<Contract>(undefined);
  const [title, setTitle] = useState("Contrato - Libertas");
  const [isVisibleDocumentModal, setIsVisibleDocumentModal] =
    useState<boolean>(false);
  const [isProductRenewalModalOpen, setIsProductRenewalModalOpen] =
    useState(false);
  const [
    isOwnershipTransferTermDraftDrawerOpen,
    setIsOwnershipTransferTermDraftDrawerOpen
  ] = useState(false);

  const [isVisibleActionModal, setIsVisibleActionModal] = useState(false);
  const [drawerState, setDrawerState] = useState<DrawerState>({
    action: null,
    isOpen: false
  });

  const { selectedBU } = useSelectedBu();

  const [requestRetryCount, setRequestRetryCount] = useState<number>(1);

  const [isButtonDisabled, setButtonDisabled] = useState(false);

  const navigate = useNavigate();
  const { id: contractId } = useParams<"id">();

  const { queryParams, changeQueryParams } = useQueryParams();
  const displayLogs = queryParams.occurrences === "true";

  const handleDisplayLogs = useCallback(
    (isOpen: boolean) => {
      changeQueryParams({ occurrences: isOpen ? "true" : undefined });
    },
    [changeQueryParams]
  );

  const actionConfirmText = useMemo(
    () => ({
      "approve-credit": selectedBU?.settings?.creditApprovalMessage
    }),
    [selectedBU?.settings?.creditApprovalMessage]
  );

  const cleanMessage = () => {
    setMessage(undefined);
  };

  const { data: invoiceInfos } = useGet<IInvoiceInfos>({
    url: `contracts/${contractId}/payment-conditions`,
    queryToken: `payment-conditions-${contractId}`,
    enabled: Boolean(contract?.id),
    cacheTime: minutesToMilliseconds(20),
    params: null
  });

  const { data: contractApprovalFlow, isFetching: isApprovalFlowLoading } =
    useGet<ContractApprovalFlowResponse>({
      url: `contracts/${contractId}/approval-flow`,
      queryToken: `contract-approval-flow-${contractId}`,
      params: null
    });

  const approvalFlow = contractApprovalFlow?.approvalFlow;
  const steps = approvalFlow?.steps ?? [];

  const requestContract = useCallback(async () => {
    const response = await getContract(contractId);

    if (response.error) {
      openModalWithSettings(
        response.error.message,
        "Você será redirecionado para pagina de contratos",
        () => {
          navigate("/contracts");

          return;
        },
        { timeOutModeProps: { timeOut: secondsToMilliseconds(2) } }
      );
    }

    return response;
  }, [contractId, navigate, openModalWithSettings]);

  const fetchData = useCallback(async () => {
    const response = await requestContract();

    if (response.error) {
      setMessage({
        message:
          response.error.message ??
          "Não foi possível localizar o contrato, por favor atualize a página e tente novamente",
        type: messageBarTypes.ERROR
      });
      setIsLoading(false);
    } else {
      const contract = response.data;

      // Can view only if !== IN_PROGRESS
      if (contract.currentStep.internalName === ContractStatus.IN_PROGRESS) {
        navigate(`/contracts/${contract.id}/edit`);
      } else {
        setCurrentStep(contract.currentStep.order);
        setNextStep(contract.nextStep);
        setContract(contract);
        setTitle(`Contrato #${contract.id} - Libertas`);
        setIsLoading(false);
      }

      return response;
    }
  }, [navigate, requestContract]);

  useEffect(() => {
    void fetchData();
  }, []);

  const persistViewContract = useCallback(async () => {
    if (requestRetryCount < 10) {
      if (!isFetching && contract?.pdfPath === null) {
        await sleep(2000 * requestRetryCount);
        setRequestRetryCount(count => count * 2);
        void fetchData();
      } else {
        setRequestRetryCount(1);
      }
    }
  }, [requestRetryCount, isFetching, contract?.pdfPath, fetchData]);

  useEffect(() => {
    void persistViewContract();
  }, [contract, isFetching, persistViewContract]);

  function closeDocumentModal() {
    setIsVisibleDocumentModal(false);
  }

  const reloadState = useCallback(
    async ({ changeFetchingState = true } = {}) => {
      if (isReloading.current) {
        return;
      }

      isReloading.current = true;

      if (changeFetchingState) {
        setIsFetching(true);
      }

      const response = await requestContract();

      if (response.error) {
        setMessage({
          message:
            response.error.message ??
            "Não foi possível localizar o contrato, por favor atualize a página e tente novamente",
          type: messageBarTypes.ERROR
        });

        if (changeFetchingState) {
          setIsFetching(false);
        }

        isReloading.current = false;

        return;
      }

      const contract = response.data;

      setCurrentStep(contract.currentStep.order);

      setNextStep(contract.nextStep);
      setContract(contract);

      if (changeFetchingState) {
        setIsFetching(false);
      }

      isReloading.current = false;
    },
    [requestContract]
  );

  const toggleVisibleActionModal = useCallback(
    (actionType?: PermissionsAction) => {
      if (ACTIONS_REQUIRING_REASON.includes(actionType)) {
        setIsVisibleActionModal(prevState => !prevState);
        setStatusChangeReason("");
        setActiveStatusChangeReason(false);
      }

      if (!actionType) {
        setIsVisibleActionModal(prevState => !prevState);
      }
    },
    []
  );

  const changeContractStatusRequest = useCallback(
    ({ action }: { action: string }) => {
      function handleFinishChangeStatus() {
        void reloadState();
        setIsVisibleActionModal(false);
        setStatusChangeReason("");
      }

      void changeContractStatus({
        contractId: contract.id,
        action,
        setMessage,
        setIsFetching,
        helperFunction: handleFinishChangeStatus,
        statusChangeReason
      });
    },
    [contract?.id, reloadState, statusChangeReason]
  );

  useEffect(() => {
    if (statusChangeReason !== "" && activeStatusChangeRequest) {
      changeContractStatusRequest({
        action: action
      });
      setActiveStatusChangeReason(false);
    }
  }, [
    statusChangeReason,
    action,
    changeContractStatusRequest,
    activeStatusChangeRequest
  ]);

  function openDrawer() {
    setDrawerState({ action: DrawerActions.VIEW, isOpen: true });
  }

  function closeDrawer() {
    setDrawerState({ action: undefined, isOpen: false });
  }

  useEffect(() => {
    setButtonDisabled(isFetching);
  }, [isFetching]);

  const shouldShowApprovalFlowShimmer = isApprovalFlowLoading && !approvalFlow;
  const shouldShowApprovalFlowProgress =
    !shouldShowApprovalFlowShimmer && approvalFlow?.steps?.length > 0;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      {message && (
        <MessageBarCustom message={message} dismissMessage={cleanMessage} />
      )}

      {(isLoading || isFetching) && (
        <>
          <ProgressIndicator
            styles={{
              root: { position: "absolute", left: 0, right: 0, top: 0 },
              itemProgress: { padding: 0 }
            }}
          />
          <Loader />
        </>
      )}

      {contract && (
        <Stack tokens={{ childrenGap: 15 }} style={{ position: "relative" }}>
          <ViewMenu
            contract={contract}
            approvalFlow={approvalFlow}
            nextStep={nextStep}
            setIsVisible={setIsVisibleDocumentModal}
            setIsContractOccurrencesDrawerVisible={() =>
              handleDisplayLogs(true)
            }
            action={action}
            setAction={setAction}
            setActionMessage={setActionMessage}
            toggleActionModal={toggleVisibleActionModal}
            contractId={contractId}
            isButtonDisabled={isButtonDisabled}
          />
          <div className="ms-Grid" dir="ltr">
            {/* SHOW CONTRACT NOTES */}

            {/* ViewProgress SHOW THE CONTRACT PROGRESS */}
            {shouldShowApprovalFlowShimmer && <ApprovalFlowShimmer />}

            {shouldShowApprovalFlowProgress && (
              <ViewProgress currentStep={currentStep} steps={steps} />
            )}

            {contract.notes &&
              contract.notes.map(note => (
                <MessageBar isMultiline key={note}>
                  {note}
                </MessageBar>
              ))}

            {/* DisplayContractInfo SHOW INFORMATIONS ABOUT THE CONTRACT */}
            <DisplayContractInfo
              clientInfoTitle={"Cooperado"}
              contractInfoTitle={"Informações"}
              className="ms-sm12 ms-xl3"
              contract={contract}
              approvalFlow={approvalFlow}
              refetchContractData={reloadState}
              nextStep={nextStep}
              setIsVisible={setIsVisibleDocumentModal}
              openDrawer={openDrawer}
              setAction={setAction}
              setActionMessage={setActionMessage}
              toggleActionModal={toggleVisibleActionModal}
              isEditingEnabled
              activationFeePaymentInfo={
                selectedBU?.settings?.activationFeePaymentInfo
              }
              onOpenRenewalProductsModal={() =>
                setIsProductRenewalModalOpen(true)
              }
              onOpenOwnershipTransferTermDraftDrawer={() =>
                setIsOwnershipTransferTermDraftDrawerOpen(true)
              }
              invoiceInfos={invoiceInfos}
            />
          </div>

          <OccurrencesPanel
            contractId={contractId}
            isOpen={displayLogs}
            onDismiss={() => handleDisplayLogs(false)}
          />

          <ClientDrawer
            isOpen={drawerState.isOpen}
            action={drawerState.action}
            contract={contract}
            onClose={closeDrawer}
            drawerContainerRef={drawerContainerRef}
          />

          <UploadDocumentModal
            closeDocumentModal={closeDocumentModal}
            isVisibleDocumentModal={isVisibleDocumentModal}
            contractId={contractId}
            requestContract={fetchData}
          />

          <ActionConfirmDialog
            isVisible={isVisibleActionModal}
            action={action}
            actionMessage={actionMessage}
            actionConfirmText={actionConfirmText[action]}
            isFetching={isFetching}
            statusChangeReason={statusChangeReason}
            onReasonChange={setStatusChangeReason}
            onConfirm={() => changeContractStatusRequest({ action })}
            onDismiss={() => {
              toggleVisibleActionModal();
              setStatusChangeReason("");
              setActiveStatusChangeReason(false);
            }}
          />

          <ContractProductsRenewalModal
            isOpen={isProductRenewalModalOpen}
            onDismiss={() => setIsProductRenewalModalOpen(false)}
            serviceCategory={contract.serviceCategory}
            service={contract.service}
            businessUnit={contract.businessUnit}
            items={contract.items}
            contractId={contract.id}
          />

          <OwnershipTransferTermDraftDrawer
            contract={contract}
            isOpen={isOwnershipTransferTermDraftDrawerOpen}
            onClose={() => setIsOwnershipTransferTermDraftDrawerOpen(false)}
            refetchContractData={() =>
              reloadState({ changeFetchingState: false })
            }
            setMessage={setMessage}
          />
        </Stack>
      )}
    </>
  );
}
